import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  DataTable,
  Button,
  Badge,
  Text,
  InlineStack,
  BlockStack,
  EmptyState,
  Spinner,
  Select,
  TextField,
  ButtonGroup,
  Icon,
  Tooltip,
  Box,
  Divider
} from '@shopify/polaris';
import {
  SearchMinor,
  ViewMinor,
  DeleteMinor,
  CustomersMajor
} from '@shopify/polaris-icons';

// Import services and constants
import { ticketsService } from '../services/ticketsService';
import { usersService } from '../services/usersService';
import CreateTicketModal from '../components/modals/CreateTicketModal';
import AssignTicketModal from '../components/modals/AssignTicketModal';
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
  TICKET_TYPE_LABELS,
  TICKET_CATEGORY_LABELS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  TICKET_SOURCE_LABELS,
  TICKET_FILTER_OPTIONS,
  PAGINATION_DEFAULTS,
  SORT_OPTIONS
} from '../constants/ticketConstants';

const TicketsPage = () => {
  const navigate = useNavigate();

  // State management
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    page: PAGINATION_DEFAULTS.PAGE,
    limit: PAGINATION_DEFAULTS.PAGE_SIZE,
    total: 0,
    pages: 1
  });

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    type: '',
    category: '',
    priority: '',
    assigned_to: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Statistics state
  const [stats, setStats] = useState(null);

  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Load tickets
  const loadTickets = useCallback(async () => {
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder
      };

      // Add filters if set
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;
      if (filters.category) params.category = filters.category;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;

      const response = await ticketsService.getTickets(params);

      if (response.success) {
        setTickets(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          pages: response.pagination?.pages || 1
        }));
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const response = await ticketsService.getTicketStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, []);

  // Load users for assignment filter
  const loadUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      const usersData = await usersService.getAssignableUsers();

      // Format users for Select component
      const formattedUsers = usersData.map(user => ({
        label: `${user.name} (${user.group_name})`,
        value: user._id.toString()
      }));

      setUsers([
        { label: 'All Users', value: '' },
        { label: 'Unassigned', value: 'unassigned' },
        ...formattedUsers
      ]);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTickets();
    loadStats();
    loadUsers();
  }, [loadTickets, loadStats, loadUsers]);

  // Setup event listeners for actions from App.js
  useEffect(() => {
    const handleRefreshEvent = () => {
      handleRefresh();
    };

    const handleCreateEvent = () => {
      handleCreateTicket();
    };

    window.addEventListener('ticketsRefresh', handleRefreshEvent);
    window.addEventListener('ticketsCreate', handleCreateEvent);

    return () => {
      window.removeEventListener('ticketsRefresh', handleRefreshEvent);
      window.removeEventListener('ticketsCreate', handleCreateEvent);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update localStorage with ticket stats for App.js subtitle
  useEffect(() => {
    if (stats) {
      localStorage.setItem('ticketsData', JSON.stringify({
        totalTickets: pagination.total,
        ...stats
      }));
    }
  }, [stats, pagination.total]);

  // Handle refresh
  const handleRefresh = () => {
    setRefreshing(true);
    loadTickets();
    loadStats();
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      page: 1 // Reset to first page on filter change
    }));
  };

  // Handle sort change
  const handleSortChange = (value) => {
    const [sortBy, sortOrder] = value.split('_');
    setFilters(prev => ({
      ...prev,
      sortBy: sortBy === 'created' || sortBy === 'updated' ? `${sortBy}_at` : sortBy,
      sortOrder
    }));
    setPagination(prev => ({
      ...prev,
      page: 1
    }));
  };

  // Handle pagination
  const handlePreviousPage = () => {
    if (pagination.page > 1) {
      setPagination(prev => ({
        ...prev,
        page: prev.page - 1
      }));
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      setPagination(prev => ({
        ...prev,
        page: prev.page + 1
      }));
    }
  };

  // Handle view ticket
  const handleViewTicket = (ticketId) => {
    console.log('Navigating to ticket:', ticketId);
    navigate(`/tickets/${ticketId}`);
  };

  // Handle delete ticket
  const handleDeleteClick = async (ticket) => {
    if (window.confirm(`Are you sure you want to delete ticket ${ticket.ticket_id}? This action cannot be undone.`)) {
      try {
        await ticketsService.deleteTicket(ticket.ticket_id);
        console.log('Ticket deleted successfully');
        loadTickets();
        loadStats();
      } catch (error) {
        console.error('Error deleting ticket:', error);
        alert('Failed to delete ticket');
      }
    }
  };

  // Handle create ticket (triggered from App.js)
  const handleCreateTicket = () => {
    setCreateModalOpen(true);
  };

  // Handle successful ticket creation
  const handleCreateTicketSuccess = () => {
    setCreateModalOpen(false);
    loadTickets();
    loadStats();
  };

  // Handle ticket creation error
  const handleCreateTicketError = (errorMessage) => {
    console.error('Error creating ticket:', errorMessage);
    alert(errorMessage || 'Failed to create ticket');
  };

  // Handle assign ticket click
  const handleAssignClick = (ticket) => {
    setSelectedTicket(ticket);
    setAssignModalOpen(true);
  };

  // Handle successful ticket assignment
  const handleAssignSuccess = () => {
    setAssignModalOpen(false);
    setSelectedTicket(null);
    loadTickets();
    loadStats();
  };

  // Handle assignment error
  const handleAssignError = (errorMessage) => {
    console.error('Error assigning ticket:', errorMessage);
    alert(errorMessage || 'Failed to assign ticket');
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Prepare table rows
  const rows = tickets.map(ticket => [
    <Button
      size="slim"
      onClick={() => handleViewTicket(ticket.ticket_id)}
    >
      <Text variant="bodyMd" fontWeight="medium">
        {ticket.ticket_id}
      </Text> 
    </Button>,
    <Box maxWidth="300px">
      <Text variant="bodyMd" as="p" truncate>
        {ticket.title}
      </Text>
    </Box>,
    <Text variant="bodyMd" as="span">
      {TICKET_TYPE_LABELS[ticket.type] || ticket.type}
    </Text>,
    <Badge
      tone={TICKET_STATUS_COLORS[ticket.status]}
      progress={ticket.status === 'in_progress' ? 'partiallyComplete' : undefined}
    >
      {TICKET_STATUS_LABELS[ticket.status] || ticket.status}
    </Badge>,
    <Badge tone={PRIORITY_COLORS[ticket.priority]}>
      {PRIORITY_LABELS[ticket.priority] || ticket.priority}
    </Badge>,
    <BlockStack gap="100">
      <Text variant="bodySm" as="p" fontWeight="medium">
        {ticket.customer?.name || ticket.customer?.email || 'N/A'}
      </Text>
      {ticket.customer?.email && (
        <Text variant="bodySm" as="p" tone="subdued">
          {ticket.customer.email}
        </Text>
      )}
    </BlockStack>,
    <Box>
      {ticket.assigned_to ? (
        <BlockStack gap="100">
          <Text variant="bodySm" as="p" fontWeight="medium">
            {ticket.assigned_to_name || 'Unknown'}
          </Text>
        </BlockStack>
      ) : (
        <Text variant="bodySm" as="span" tone="subdued">
          Unassigned
        </Text>
      )}
    </Box>,
    <Text variant="bodySm" as="span" tone="subdued">
      {formatDate(ticket.created_at)}
    </Text>,
    <ButtonGroup>
      <Tooltip content="View ticket">
        <Button
          size="slim"
          icon={ViewMinor}
          onClick={() => handleViewTicket(ticket.ticket_id)}
        />
      </Tooltip>
      <Tooltip content={ticket.assigned_to ? "Reassign ticket" : "Assign ticket"}>
        <Button
          size="slim"
          icon={CustomersMajor}
          onClick={() => handleAssignClick(ticket)}
        />
      </Tooltip>
      <Tooltip content="Delete ticket">
        <Button
          size="slim"
          icon={DeleteMinor}
          onClick={() => handleDeleteClick(ticket)}
          tone="critical"
        />
      </Tooltip>
    </ButtonGroup>
  ]);

  // Empty state
  const emptyStateMarkup = (
    <EmptyState
      heading="No tickets found"
      action={{
        content: 'Create ticket',
        onAction: handleCreateTicket
      }}
      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
    >
      <p>Create your first support ticket to get started.</p>
    </EmptyState>
  );

  // Statistics cards
  const statisticsMarkup = stats && (
    <InlineStack gap="400" blockAlign="start">
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h3">Total Tickets</Text>
          <Text variant="heading2xl" as="p">{stats.total || 0}</Text>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h3">Open</Text>
          <Text variant="heading2xl" as="p" tone="info">{stats.open || 0}</Text>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h3">In Progress</Text>
          <Text variant="heading2xl" as="p" tone="attention">{stats.in_progress || 0}</Text>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h3">Resolved</Text>
          <Text variant="heading2xl" as="p" tone="success">{stats.resolved || 0}</Text>
        </BlockStack>
      </Card>
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h3">Overdue</Text>
          <Text variant="heading2xl" as="p" tone="critical">{stats.overdue || 0}</Text>
        </BlockStack>
      </Card>
    </InlineStack>
  );

  return (
    <>
      {statisticsMarkup}

      <Card>
        <BlockStack gap="400">
          {/* Filters */}
              <InlineStack gap="400" align="space-between">
                <Box width="300px">
                  <TextField
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={(value) => handleFilterChange('search', value)}
                    prefix={<Icon source={SearchMinor} />}
                    clearButton
                    onClearButtonClick={() => handleFilterChange('search', '')}
                  />
                </Box>
                <InlineStack gap="300">
                  <Select
                    label="Status"
                    labelInline
                    options={[
                      { label: 'All Statuses', value: '' },
                      ...TICKET_FILTER_OPTIONS.STATUS
                    ]}
                    value={filters.status}
                    onChange={(value) => handleFilterChange('status', value)}
                  />
                  <Select
                    label="Type"
                    labelInline
                    options={[
                      { label: 'All Types', value: '' },
                      ...TICKET_FILTER_OPTIONS.TYPE
                    ]}
                    value={filters.type}
                    onChange={(value) => handleFilterChange('type', value)}
                  />
                  <Select
                    label="Priority"
                    labelInline
                    options={[
                      { label: 'All Priorities', value: '' },
                      ...TICKET_FILTER_OPTIONS.PRIORITY
                    ]}
                    value={filters.priority}
                    onChange={(value) => handleFilterChange('priority', value)}
                  />
                  <Select
                    label="Assigned To"
                    labelInline
                    options={users}
                    value={filters.assigned_to}
                    onChange={(value) => handleFilterChange('assigned_to', value)}
                    disabled={usersLoading}
                  />
                  <Select
                    label="Sort by"
                    labelInline
                    options={SORT_OPTIONS}
                    value={`${filters.sortBy.replace('_at', '')}_${filters.sortOrder}`}
                    onChange={handleSortChange}
                  />
                </InlineStack>
              </InlineStack>

              <Divider />

              {/* Table */}
              {loading ? (
                <Box padding="800">
                  <InlineStack align="center">
                    <Spinner size="large" />
                  </InlineStack>
                </Box>
              ) : tickets.length === 0 ? (
                emptyStateMarkup
              ) : (
                <>
                  <DataTable
                    columnContentTypes={[
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text',
                      'text'
                    ]}
                    headings={[
                      'Ticket ID',
                      'Title',
                      'Type',
                      'Status',
                      'Priority',
                      'Customer',
                      'Assigned To',
                      'Created',
                      'Actions'
                    ]}
                    rows={rows}
                  />

                  {/* Pagination */}
                  <InlineStack align="space-between">
                    <Text variant="bodySm" tone="subdued">
                      Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} -{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                    </Text>
                    <ButtonGroup>
                      <Button
                        disabled={pagination.page === 1}
                        onClick={handlePreviousPage}
                      >
                        Previous
                      </Button>
                      <Button
                        disabled={pagination.page >= pagination.pages}
                        onClick={handleNextPage}
                      >
                        Next
                      </Button>
                    </ButtonGroup>
                  </InlineStack>
                </>
              )}
            </BlockStack>
          </Card>

      <CreateTicketModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateTicketSuccess}
        onError={handleCreateTicketError}
      />

      <AssignTicketModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        ticketId={selectedTicket?.ticket_id}
        currentAssignee={selectedTicket?.assigned_to}
        onSuccess={handleAssignSuccess}
        onError={handleAssignError}
      />
    </>
  );
};

export default TicketsPage;
